import * as React from "react";
import { insertLayer, nextLayerOrder, useFocusTrap, type LayerEntry } from "@/hooks/use-focus-trap";
import { fromInputMethod } from "@/lib/connection-label";

/**
 * A modal overlay that meets the DS Modal contract without changing the DOM.
 *
 * Renders exactly the `<div>` it replaces — same class names, same handlers —
 * and adds the things the contract requires and hand-rolled overlays almost
 * always miss: Tab is trapped inside, background scroll is locked, focus
 * returns to whatever opened it, and Esc closes the layer.
 *
 *   <div className="modal-overlay" onClick={onClose}>   ->
 *   <FocusScope className="modal-overlay" onClick={onClose}>
 *
 * Esc is delivered as a synthetic click on the scrim element itself, so it
 * runs the caller's existing close handler — including any
 * `e.target === e.currentTarget` guard or in-flight (`isPending`) check —
 * rather than a second close path that can drift from it. Only the topmost
 * scope reacts (a shared stack), and a scope stands down while focus is
 * outside it (a portalled Radix menu above it owns the key until it closes).
 */

const escStack: LayerEntry[] = [];

export type FocusScopeProps = React.HTMLAttributes<HTMLDivElement> & {
  /** Defaults to true. False keeps the same element in the tree but drops the
   *  trap, the scroll lock and the Esc handling, for a surface that is only
   *  sometimes modal (the agent's review drawer is a column on wide rows and
   *  a covering layer on narrow ones). Switching the element type instead
   *  would remount the children, and with them an iframe's whole page. */
  trapped?: boolean;
};

export const FocusScope = React.forwardRef<HTMLDivElement, FocusScopeProps>(
  function FocusScope({ trapped = true, ...props }, _forwarded) {
    // Ranked by when the scope mounted, not when it became trapped, so a
    // surface that turns modal on a resize stays under layers opened after it.
    const [order] = React.useState(nextLayerOrder);
    const ref = useFocusTrap<HTMLDivElement>(trapped, order);

    React.useEffect(() => {
      if (!trapped) return;
      const el = ref.current;
      if (!el) return;
      insertLayer(escStack, { node: el, order });
      const onKey = (e: KeyboardEvent) => {
        // The IME's own Escape cancels the candidate the user is picking. Closing
        // the layer on it discards whatever they were typing instead - a connection
        // name in ConnectModal, an invite in the slide-over.
        if (e.key !== "Escape" || e.defaultPrevented || fromInputMethod(e)) return;
        if (escStack[escStack.length - 1]?.node !== el) return;
        const active = document.activeElement;
        if (active && active !== document.body && !el.contains(active)) return;
        e.stopPropagation();
        el.dispatchEvent(new MouseEvent("mousedown", { bubbles: true }));
        el.click();
      };
      document.addEventListener("keydown", onKey);
      return () => {
        const i = escStack.findIndex((l) => l.node === el);
        if (i !== -1) escStack.splice(i, 1);
        document.removeEventListener("keydown", onKey);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trapped]);

    return <div ref={ref} {...props} />;
  },
);
